import { LoadingContainer } from 'peach/components'
import { useHistory, useParams } from 'peach/router'

import { DynamicForm, Page, PageHeader } from 'core/components'
import { useCreateWorkflowType, useGetWorkflowStepTypes } from 'core/types'
import {
  workflowTypeSchema,
  type StaticFieldSchema,
} from 'core/utils/generated/formSchemas'

const CreateWorkflowType = () => {
  const history = useHistory()
  const { companyId } = useParams<{ companyId?: string }>()

  const { mutateAsync: createWorkflowType } = useCreateWorkflowType({
    companyId,
  })

  // Fetch available workflow step types to build options for steps
  const { data: stepTypes } = useGetWorkflowStepTypes({ companyId })

  const stepOptions: Array<{
    label: string
    value: string
    sublabel?: string
  }> = Array.isArray(stepTypes)
    ? (stepTypes as Array<any>)
        .map((s) => {
          const id = s?.id as string | undefined
          if (!id) return null
          const desc = (s?.descriptionShort ||
            s?.descriptionLong ||
            '') as string
          const truncated = desc
            ? desc.length > 70
              ? desc.slice(0, 70) + '…'
              : desc
            : undefined
          return truncated
            ? { label: id, value: id, sublabel: truncated }
            : { label: id, value: id }
        })
        .filter(
          (v): v is { label: string; value: string; sublabel?: string } =>
            v !== null,
        )
    : []

  // Optionally inject value options into the schema for completeness
  const enhancedSchema: Array<StaticFieldSchema> = (
    workflowTypeSchema as Array<StaticFieldSchema>
  ).map((f) => (f.key === 'steps' ? { ...f, options: stepOptions } : f))

  return (
    <LoadingContainer isLoading={false}>
      <Page variant='full'>
        <PageHeader title='Create Workflow Type' />
        <DynamicForm
          data={{}}
          fieldEditMode='all'
          fieldOverrides={{ steps: 'MultiSelect' }}
          onSubmit={async (payload: any) => {
            const mappedSteps = Array.isArray(payload?.steps)
              ? (payload.steps as Array<any>).map((it) =>
                  typeof it === 'string' ? it : it?.id ?? it?.value ?? it,
                )
              : payload?.steps

            const res = await createWorkflowType({
              ...payload,
              steps: mappedSteps,
            })
            const id = (res as any)?.data?.id as string | undefined
            if (id) {
              history.push(`/companies/${companyId}/workflow-types/${id}`)
            } else {
              history.push(`/companies/${companyId}/workflow-types`)
            }
          }}
          schema={enhancedSchema}
          submitText='Create'
          validationRules={{
            name: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'name is required',
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default CreateWorkflowType
